import { TextareaHTMLAttributes, forwardRef, useCallback, useEffect, useRef } from "react";

import { InputStyles } from "./input-styles";
import { cn } from "./utils";

const useAutoSize = (enabled = false) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    if (enabled && textareaRef.current) {
      textareaRef.current.style.height = "0px";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      adjustHeight();
      window.addEventListener("resize", adjustHeight);
      return () => window.removeEventListener("resize", adjustHeight);
    }
  }, [enabled, adjustHeight]);

  return {
    textareaRef,
    adjustHeight,
  };
};

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoSize?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoSize = false, ...props }, ref) => {
    const { textareaRef, adjustHeight } = useAutoSize(autoSize);

    const handleRef = (element: HTMLTextAreaElement | null) => {
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
      textareaRef.current = element;
      if (element && autoSize) {
        adjustHeight();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoSize) {
        adjustHeight();
      }
      props.onChange?.(e);
    };

    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (autoSize) {
        adjustHeight();
      }
      props.onInput?.(e);
    };

    return (
      <textarea
        className={cn(InputStyles.Base, InputStyles.Textarea, className)}
        ref={handleRef}
        onChange={handleChange}
        onInput={handleInput}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
